/**
 * The accounts that have signed in ON THIS DEVICE — #1053 slice 4b.
 *
 * ── why this file has NO imports, and that is the security property ────────
 *
 * The obvious account switcher is one query long and it must not ship. #1053
 * comment 17850 records the ruling: read the caller's `core.app_user.person_id`,
 * find the sibling `app_user` rows sharing it, offer those accounts. That link
 * is an MDM *data-quality assertion* — `core.propose_party_matches` scores
 * candidates, a steward confirms, and `access.backfill_user_person` links a
 * login to a person by e-mail equality alone. Every one of those is a heuristic
 * about who somebody probably is, and **no unmerge door exists anywhere in the
 * tree** (`core.reject_match` on an already-confirmed pair leaves the identity
 * edge and the golden flag exactly as they were). Wire a switcher to it and a
 * wrong merge does not produce a wrong report — it offers a stranger's account
 * and invites the user to authenticate into it.
 *
 * So the correct shape makes **no identity claim at all**: the client remembers
 * which accounts have signed in on this device, and *the device is the
 * evidence*. The server is never asked "which accounts is this person?", so no
 * server-side link is ever on the path.
 *
 * That is why this module imports nothing — not `@solum/sdk`, not `./session`,
 * not `fetch`. It is asserted rather than merely intended:
 * `accounts.test.ts` walks this file's AST and fails on an import declaration
 * or a network call, with a vacuity arm proving the matcher fires. A structural
 * refusal, because "we did not mean to ask the server" is not a control.
 *
 * ── what is stored, and what is deliberately NOT ───────────────────────────
 *
 * Addresses and ids. **No tokens.** A parked session for a second account would
 * let a switch resume without re-authenticating — comment 17850 sketches it —
 * but it multiplies the credential-at-rest count that `session.ts`'s STORAGE
 * note reviewed and accepted for exactly ONE session (SEC-08 / #371, three
 * named mitigations: one-hour access tokens, rotation on every use, family
 * invalidation on reuse). Widening that is a security-posture decision on an
 * identity surface, and neither the Security-Model page nor the ADR register
 * carries it. So the switcher can only ever OFFER, never GRANT — Christopher's
 * own condition on that comment, in its strongest form: every switch
 * re-authenticates. The resume half is a separate, ruled decision.
 *
 * Signing out clears the whole list, so a shared machine leaks e-mail addresses
 * at worst — and only while somebody is signed in. **A SWITCH does not clear
 * it**, which is the one distinction that makes the feature work: leaving a
 * session to sign into another is not leaving the device.
 *
 * ── two storages, two lifetimes, and they are not interchangeable ──────────
 *
 * The ROSTER is per DEVICE (`localStorage`): it must survive the tab that wrote
 * it, because the account you signed into yesterday is still an account on this
 * machine.
 *
 * The PENDING SWITCH is per TAB (`sessionStorage`): "I am on my way to sign in
 * as X" is a fact about one browsing context. In `localStorage` a switch
 * started in one tab would pre-fill another tab's sign-in form with an address
 * nobody in that tab chose — the same class of defect as a persona selection
 * leaking across tabs.
 */

/** One account that has signed in on this device. Addresses and ids only. */
export interface DeviceAccount {
  /** GoTrue's `sub` — the login id, never `core.person.id`. See the header. */
  userId: string
  email: string | null
  /** epoch ms, so the list can be offered most-recent-first */
  lastSignedInAt: number
}

/**
 * The one storage seam, so a host with no Web Storage (an MV3 worker, a Node
 * unit run, a browser in private mode that throws on access) degrades to
 * remembering nothing rather than throwing. Same reasoning as
 * `SessionStorageAdapter`; a smaller contract because there is no async host
 * for this yet and inventing one would be speculative.
 */
export interface DeviceStorage {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

const ROSTER_KEY = 'solum.accounts'
const PENDING_KEY = 'solum.switch-target'

/**
 * A hard cap, because an unbounded list is a growing pile of addresses on a
 * shared machine and a menu nobody can read. Eight is the largest roster that
 * still fits the menu without its own scroll region; the oldest entry falls off.
 */
export const ROSTER_LIMIT = 8

/** `globalThis.localStorage`, or a store that remembers nothing. */
export function deviceStorage(): DeviceStorage {
  return {
    get(key) { try { return globalThis.localStorage?.getItem(key) ?? null } catch { return null } },
    set(key, value) { try { globalThis.localStorage?.setItem(key, value) } catch { /* no storage */ } },
    remove(key) { try { globalThis.localStorage?.removeItem(key) } catch { /* no storage */ } },
  }
}

/** `globalThis.sessionStorage` — this TAB only. See the header. */
export function tabStorage(): DeviceStorage {
  return {
    get(key) { try { return globalThis.sessionStorage?.getItem(key) ?? null } catch { return null } },
    set(key, value) { try { globalThis.sessionStorage?.setItem(key, value) } catch { /* no storage */ } },
    remove(key) { try { globalThis.sessionStorage?.removeItem(key) } catch { /* no storage */ } },
  }
}

/** Parse defensively: this is user-writable storage, not a wire format we own. */
function parse(raw: string | null): DeviceAccount[] {
  if (!raw) return []
  try {
    const value = JSON.parse(raw) as unknown
    if (!Array.isArray(value)) return []
    return value
      .filter((row): row is DeviceAccount => {
        const a = row as Partial<DeviceAccount> | null
        return typeof a?.userId === 'string' && a.userId !== '' && typeof a.lastSignedInAt === 'number'
      })
      .map((a) => ({
        userId: a.userId,
        email: typeof a.email === 'string' ? a.email : null,
        lastSignedInAt: a.lastSignedInAt,
      }))
  } catch { return [] }
}

/**
 * The device's own list of accounts. Framework-free and storage-injected, like
 * everything else in this package: the React binding subscribes, it does not own.
 */
export class AccountRoster {
  private listeners = new Set<() => void>()

  constructor(
    private readonly storage: DeviceStorage = deviceStorage(),
    private readonly tab: DeviceStorage = tabStorage(),
  ) {}

  /** Most recently signed-in first. */
  list(): DeviceAccount[] {
    return parse(this.storage.get(ROSTER_KEY)).sort((a, b) => b.lastSignedInAt - a.lastSignedInAt)
  }

  find(userId: string): DeviceAccount | null {
    return this.list().find((a) => a.userId === userId) ?? null
  }

  /**
   * Record a successful authentication. Called from `AuthClient.set()` — the
   * ONE place a session becomes current — so no sign-in path can be forgotten:
   * password, sign-up grant, MFA verification and a background refresh all land
   * there. Re-signing in updates the address and the timestamp rather than
   * growing a second row for the same login.
   */
  remember(account: { userId: string; email: string | null }, at: number): void {
    if (!account.userId) return
    const rest = this.list().filter((a) => a.userId !== account.userId)
    const next = [{ userId: account.userId, email: account.email, lastSignedInAt: at }, ...rest]
      .slice(0, ROSTER_LIMIT)
    this.storage.set(ROSTER_KEY, JSON.stringify(next))
    this.emit()
  }

  /**
   * Sign-out, and ONLY sign-out. A switch deliberately does not call this: the
   * whole point is that the device still remembers both addresses while nobody
   * is signed in to either.
   */
  forgetAll(): void {
    this.storage.remove(ROSTER_KEY)
    this.clearPendingSwitch()
    this.emit()
  }

  /* ── the pending switch: this TAB is on its way to sign in as somebody ── */

  /** The address this tab is switching to, for the sign-in form to pre-fill. */
  pendingSwitch(): string | null {
    const value = this.tab.get(PENDING_KEY)
    return value === null || value === '' ? null : value
  }

  setPendingSwitch(email: string | null): void {
    if (email) this.tab.set(PENDING_KEY, email)
    else this.tab.remove(PENDING_KEY)
    this.emit()
  }

  clearPendingSwitch(): void {
    this.tab.remove(PENDING_KEY)
    this.emit()
  }

  /** The external-store contract, so a host can re-render without polling. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  private emit(): void { for (const listener of this.listeners) listener() }
}
